import React from 'react';
import { Filter, X } from 'lucide-react';

interface ProductFiltersProps {
  filters: {
    isBundle?: boolean | number;
    isActive?: boolean | number;
    isFavorite?: boolean | number;
  };
  onFilterChange: (filters: {
    isBundle?: boolean | number;
    isActive?: boolean | number;
    isFavorite?: boolean | number;
  }) => void;
  onClearFilters: () => void;
}

const ProductFilters: React.FC<ProductFiltersProps> = ({ 
  filters, 
  onFilterChange, 
  onClearFilters 
}) => {
  const hasActiveFilters = Object.values(filters).some(value => value !== undefined);

  const handleFilterChange = (key: keyof typeof filters, value: string) => {
    let filterValue: boolean | number | undefined;
    
    if (value === '') {
      filterValue = undefined;
    } else if (value === 'true' || value === '1') {
      filterValue = 1; // Use 1 for API compatibility
    } else if (value === 'false' || value === '0') {
      filterValue = 0; // Use 0 for API compatibility
    } else {
      filterValue = undefined;
    }
    
    onFilterChange({
      ...filters,
      [key]: filterValue
    });
  };

  const handleFilterRemove = (key: keyof typeof filters) => {
    onFilterChange({
      ...filters,
      [key]: undefined
    });
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Filter className="h-5 w-5 text-gray-400 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">Filters</h3>
        </div>
        {hasActiveFilters && (
          <button
            onClick={onClearFilters}
            className="flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <X className="h-4 w-4 mr-1" />
            Clear all
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Bundle Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Bundle Status
          </label>
          <select
            value={filters.isBundle === undefined ? '' : (filters.isBundle === 1 ? 'true' : 'false')}
            onChange={(e) => handleFilterChange('isBundle', e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All</option>
            <option value="true">Bundle only</option>
            <option value="false">Non-bundle only</option>
          </select>
        </div>

        {/* Active Status Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Active Status
          </label>
          <select
            value={filters.isActive === undefined ? '' : (filters.isActive === 1 ? 'true' : 'false')}
            onChange={(e) => handleFilterChange('isActive', e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All</option>
            <option value="true">Active only</option>
            <option value="false">Inactive only</option>
          </select>
        </div>

        {/* Favorite Status Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Favorite Status
          </label>
          <select
            value={filters.isFavorite === undefined ? '' : (filters.isFavorite === 1 ? 'true' : 'false')}
            onChange={(e) => handleFilterChange('isFavorite', e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All</option>
            <option value="true">Favorite only</option>
            <option value="false">Non-favorite only</option>
          </select>
        </div>
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex flex-wrap gap-2">
            {filters.isBundle !== undefined && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                Bundle: {filters.isBundle === 1 ? 'Yes' : 'No'}
                <button
                  onClick={() => handleFilterRemove('isBundle')}
                  className="ml-2 text-blue-600 hover:text-blue-800"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {filters.isActive !== undefined && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                Active: {filters.isActive === 1 ? 'Yes' : 'No'}
                <button
                  onClick={() => handleFilterRemove('isActive')}
                  className="ml-2 text-green-600 hover:text-green-800"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {filters.isFavorite !== undefined && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-yellow-100 text-yellow-800">
                Favorite: {filters.isFavorite === 1 ? 'Yes' : 'No'}
                <button
                  onClick={() => handleFilterRemove('isFavorite')}
                  className="ml-2 text-yellow-600 hover:text-yellow-800"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductFilters;
